import { EVENTS } from "const";

/**
 * An internal type-safe pub/sub bus.
 */

class EventEmitter {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * Subscribe to an application event.
   * @param {string} event - The event name.
   * @param {Function} callback - The listener function.
   * @returns {Function} Unsubscribe cleanup function.
   */
  on(event, callback) {
    if (!event) return () => {};
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const eventSet = this.listeners.get(event);
    eventSet.add(callback);

    // Return unsubscribe function
    return () => {
      eventSet.delete(callback);
    };
  }

  /**
   * Emit an application event to all active subscribers.
   * @param {string} event - The event name.
   * @param {*} [data] - Optional payload passed to listeners.
   */
  emit(event, data) {
    if (!event) return;
    const eventSet = this.listeners.get(event);
    if (eventSet) {
      eventSet.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[AppEvents] Error in listener for event "${event}":`, error);
        }
      });
    }
  }

  /**
   * Remove all listeners for a specific event or all events.
   * @param {string} [event] - Event name to clear. Clears all if omitted.
   */
  removeAllListeners(event) {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

export const eventsEmitter = new EventEmitter();

// Global DOM event proxies -> emit events over eventsEmitter
if (typeof window !== "undefined") {
  window.addEventListener(
    "keydown",
    (e) => {
      if (EVENTS?.KEY_DOWN) {
        eventsEmitter.emit(EVENTS.KEY_DOWN, e);
      }
    },
    true,
  );

  window.addEventListener("popstate", (e) => {
    if (EVENTS?.POP_STATE) {
      eventsEmitter.emit(EVENTS.POP_STATE, e);
    }
  });

  if (typeof document !== "undefined") {
    document.addEventListener("mousedown", (e) => {
      if (EVENTS?.MOUSE_DOWN) {
        eventsEmitter.emit(EVENTS.MOUSE_DOWN, e);
      }
    });
  }
}
